import React from 'react';
import './ProductionAnalyticsTab.css';

const fmt = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '$0.00');
const fmtPct = (n) => (typeof n === 'number' ? `${(n * 100).toFixed(1)}%` : '—');

const ProductionAnalyticsTab = ({ capacity, products = [] }) => {
  const activeProducts = products.filter((p) => p.is_active);
  const avgWholesaleMargin = activeProducts.length
    ? activeProducts.reduce((s, p) => s + (p.wholesale_margin || 0), 0) / activeProducts.length
    : null;
  const avgRetailMargin = activeProducts.length
    ? activeProducts.reduce((s, p) => s + (p.retail_margin || 0), 0) / activeProducts.length
    : null;
  const avgCogs = activeProducts.length
    ? activeProducts.reduce((s, p) => s + (p.total_cogs || 0), 0) / activeProducts.length
    : null;
  const topMarginProducts = [...activeProducts]
    .sort((a, b) => (b.wholesale_margin || 0) - (a.wholesale_margin || 0))
    .slice(0, 5);
  const bottomMarginProducts = [...activeProducts]
    .sort((a, b) => (a.wholesale_margin || 0) - (b.wholesale_margin || 0))
    .slice(0, 5);

  return (
    <div className="analytics-tab">
      {/* Summary stats row */}
      {activeProducts.length > 0 && (
        <div className="analytics-stat-row">
          <div className="analytics-stat-card">
            <span className="analytics-stat-label">Active Products</span>
            <span className="analytics-stat-value">{activeProducts.length}</span>
          </div>
          <div className="analytics-stat-card">
            <span className="analytics-stat-label">Avg Wholesale Margin</span>
            <span className={`analytics-stat-value ${avgWholesaleMargin >= 0.3 ? 'good' : avgWholesaleMargin >= 0.15 ? 'warn' : 'bad'}`}>
              {fmtPct(avgWholesaleMargin)}
            </span>
          </div>
          <div className="analytics-stat-card">
            <span className="analytics-stat-label">Avg Retail Margin</span>
            <span className={`analytics-stat-value ${avgRetailMargin >= 0.4 ? 'good' : avgRetailMargin >= 0.2 ? 'warn' : 'bad'}`}>
              {fmtPct(avgRetailMargin)}
            </span>
          </div>
          <div className="analytics-stat-card">
            <span className="analytics-stat-label">Avg COGS</span>
            <span className="analytics-stat-value">{fmt(avgCogs)}</span>
          </div>
        </div>
      )}

      <div className="analytics-tab-grid">
        {/* Price Overview */}
        <div className="analytics-card">
          <h3 className="analytics-card-title">Price Overview</h3>
          {activeProducts.length === 0 ? (
            <div className="analytics-empty"><p>No product data on this page.</p></div>
          ) : (() => {
            const wsProducts  = activeProducts.filter((p) => p.wholesale_price > 0);
            const retProducts = activeProducts.filter((p) => p.retail_price > 0);
            const avgWs  = wsProducts.length  ? wsProducts.reduce((s, p) => s + p.wholesale_price, 0) / wsProducts.length  : null;
            const avgRet = retProducts.length ? retProducts.reduce((s, p) => s + p.retail_price,   0) / retProducts.length : null;
            const minWs  = wsProducts.length  ? Math.min(...wsProducts.map((p) => p.wholesale_price))  : null;
            const maxWs  = wsProducts.length  ? Math.max(...wsProducts.map((p) => p.wholesale_price))  : null;
            const minRet = retProducts.length ? Math.min(...retProducts.map((p) => p.retail_price))   : null;
            const maxRet = retProducts.length ? Math.max(...retProducts.map((p) => p.retail_price))   : null;
            const rows = [
              { label: 'Avg Wholesale Price', value: fmt(avgWs),  sub: minWs != null ? `${fmt(minWs)} – ${fmt(maxWs)}` : null },
              { label: 'Avg Retail Price',    value: fmt(avgRet), sub: minRet != null ? `${fmt(minRet)} – ${fmt(maxRet)}` : null },
              { label: 'Avg Price Spread',    value: (avgWs != null && avgRet != null) ? fmt(avgRet - avgWs) : '—', sub: 'retail minus wholesale' },
            ];
            return (
              <div className="price-overview-list">
                {rows.map((r) => (
                  <div key={r.label} className="price-overview-item">
                    <div className="price-overview-left">
                      <span className="price-overview-label">{r.label}</span>
                      {r.sub && <span className="price-overview-sub">{r.sub}</span>}
                    </div>
                    <span className="price-overview-value">{r.value}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Top margin products */}
        <div className="analytics-card">
          <h3 className="analytics-card-title">Best Margins (Wholesale)</h3>
          {topMarginProducts.length === 0 ? (
            <div className="analytics-empty"><p>No product data on this page.</p></div>
          ) : (
            <div className="margin-list">
              {topMarginProducts.map((p) => (
                <div key={p.id} className="margin-item">
                  <span className="margin-product">{p.flavor}</span>
                  <div className="margin-bar-wrap">
                    <div className="margin-bar-track">
                      <div
                        className="margin-bar-fill good"
                        style={{ width: `${Math.min((p.wholesale_margin || 0) * 100 * 2, 100)}%` }}
                      />
                    </div>
                    <span className="margin-pct good">{fmtPct(p.wholesale_margin)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom margin products */}
        <div className="analytics-card">
          <h3 className="analytics-card-title">Lowest Margins (Wholesale)</h3>
          {bottomMarginProducts.length === 0 ? (
            <div className="analytics-empty"><p>No product data on this page.</p></div>
          ) : (
            <div className="margin-list">
              {bottomMarginProducts.map((p) => (
                <div key={p.id} className="margin-item">
                  <span className="margin-product">{p.flavor}</span>
                  <div className="margin-bar-wrap">
                    <div className="margin-bar-track">
                      <div
                        className={`margin-bar-fill ${p.wholesale_margin >= 0.15 ? 'warn' : 'bad'}`}
                        style={{ width: `${Math.min((p.wholesale_margin || 0) * 100 * 2, 100)}%` }}
                      />
                    </div>
                    <span className={`margin-pct ${p.wholesale_margin >= 0.15 ? 'warn' : 'bad'}`}>
                      {fmtPct(p.wholesale_margin)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* COGS breakdown across products */}
        <div className="analytics-card">
          <h3 className="analytics-card-title">Avg Cost Composition</h3>
          {activeProducts.length === 0 ? (
            <div className="analytics-empty"><p>No product data on this page.</p></div>
          ) : (() => {
            const avgCoffee     = activeProducts.reduce((s, p) => s + (p.coffee_cost || 0), 0) / activeProducts.length;
            const avgIngredient = activeProducts.reduce((s, p) => s + (p.ingredient_cost || 0), 0) / activeProducts.length;
            const avgCan        = activeProducts.reduce((s, p) => s + (p.can_cost || 0), 0) / activeProducts.length;
            const avgPacking    = activeProducts.reduce((s, p) => s + (p.packing_cost || 0), 0) / activeProducts.length;
            const total         = avgCoffee + avgIngredient + avgCan + avgPacking;
            const segments = [
              { label: 'Coffee',      value: avgCoffee,     cls: 'coffee' },
              { label: 'Ingredients', value: avgIngredient, cls: 'ingredient' },
              { label: 'Can',         value: avgCan,        cls: 'can' },
              { label: 'Packing',     value: avgPacking,    cls: 'packing' },
            ];
            return (
              <div className="cogs-breakdown">
                <div className="cogs-bar">
                  {segments.map((s) =>
                    s.value > 0 ? (
                      <div
                        key={s.cls}
                        className={`cogs-seg ${s.cls}`}
                        style={{ width: `${(s.value / total) * 100}%` }}
                        title={`${s.label}: ${fmt(s.value)}`}
                      />
                    ) : null
                  )}
                </div>
                <div className="cogs-legend">
                  {segments.map((s) => (
                    <div key={s.cls} className="cogs-legend-item">
                      <i className={`cogs-dot ${s.cls}`} />
                      <span>{s.label}</span>
                      <strong>{fmt(s.value)}</strong>
                    </div>
                  ))}
                </div>
                <div className="cogs-total">Avg Total COGS: <strong>{fmt(total)}</strong></div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ProductionAnalyticsTab;
